import { Button, Card, Form, Input, InputNumber, Modal, Select, Switch, Table, Tag, Upload, notification } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { BiDownload, BiPlus, BiUpload } from "react-icons/bi";
import Header from "shared/components/Header";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import cuid from "cuid";
import * as XLSX from "xlsx";

const List = () => {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  const { data, refetch } = useQuery(graphql.queries.CRM_LEADS, {
    variables: {},
    fetchPolicy: "network-only",
  });
  const [createLead] = useMutation(graphql.mutations.CREATE_CRM_LEAD);
  const [deleteLead] = useMutation(graphql.mutations.DELETE_CRM_LEAD);

  const leads = get(data, "crmLeads.data") || [];
  const count = get(data, "crmLeads.count", 0);

  const filtered = search
    ? leads.filter((l) =>
        `${l.email} ${l.firstName} ${l.lastName} ${l.companyName}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : leads;

  const handleCreate = async () => {
    const values = await form.validateFields();
    await createLead({
      variables: {
        data: {
          id: cuid(),
          email: values.email,
          firstName: values.firstName || null,
          lastName: values.lastName || null,
          companyName: values.companyName || null,
          phone: values.phone || null,
          jobTitle: values.jobTitle || null,
          hasContratSante: values.hasContratSante || false,
          hasContratPrevoyance: values.hasContratPrevoyance || false,
          hasContratDeces: values.hasContratDeces || false,
          cotisationMensuelle: values.cotisationMensuelle || null,
          regime: values.regime || null,
          departement: values.departement || null,
        },
      },
    });
    form.resetFields();
    setCreateOpen(false);
    await refetch();
  };

  const handleDelete = async (id) => {
    await deleteLead({ variables: { where: { id } } });
    await refetch();
  };

  const parseExcel = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const wb = XLSX.read(e.target.result, { type: "array" });
        resolve(XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  const handleImport = async (file) => {
    try {
      const rows = await parseExcel(file);
      let added = 0;
      let skipped = 0;
      const existingEmails = new Set(leads.map((l) => (l.email || "").toLowerCase()));

      for (const row of rows) {
        const email = String(row.Email || row.email || "").trim();
        if (!email || existingEmails.has(email.toLowerCase())) {
          skipped++;
          continue;
        }
        try {
          await createLead({
            variables: {
              data: {
                id: cuid(),
                email,
                firstName: row["Prénom"] || row.firstName ? String(row["Prénom"] || row.firstName) : null,
                lastName: row.Nom || row.lastName ? String(row.Nom || row.lastName) : null,
                companyName: row.Entreprise || row.company ? String(row.Entreprise || row.company) : null,
                phone: row["Téléphone"] || row.phone ? String(row["Téléphone"] || row.phone) : null,
                jobTitle: row.Poste || row.jobTitle ? String(row.Poste || row.jobTitle) : null,
              },
            },
          });
          existingEmails.add(email.toLowerCase());
          added++;
        } catch (err) {
          skipped++;
        }
      }
      await refetch();
      notification.success({ message: `Import : ${added} ajouté(s), ${skipped} ignoré(s)` });
    } catch (err) {
      notification.error({ message: `Échec : ${err.message}` });
    }
    return false;
  };

  const handleDownloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([
      { Email: "", "Prénom": "", Nom: "", Entreprise: "", "Téléphone": "", Poste: "" },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    XLSX.writeFile(wb, "leads-template.xlsx");
  };

  return (
    <>
      <Header key="header" title={`Leads (${count})`} actions={[
        { text: "Nouveau lead", onClick: () => setCreateOpen(true), type: "primary", icon: <BiPlus size={18} /> },
      ]} />

      <div className="m-bottom-12 d-flex justify--between" style={{ alignItems: "center" }}>
        <div className="d-flex" style={{ gap: "8px" }}>
          <Button icon={<BiDownload size={14} />} size="small" onClick={handleDownloadTemplate}>
            Modèle
          </Button>
          <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleImport}>
            <Button icon={<BiUpload size={14} />} size="small">Importer</Button>
          </Upload>
        </div>
        <Input.Search
          placeholder="Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
          style={{ width: 250 }}
        />
      </div>

      <Table dataSource={filtered} rowKey="id" size="small" pagination={{ pageSize: 25 }} columns={[
        {
          title: "Lead",
          render: (_, r) => (
            <div>
              <div style={{ fontWeight: 500 }}>{r.firstName} {r.lastName}</div>
              <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "monospace" }}>{r.email}</div>
            </div>
          ),
        },
        { title: "Entreprise", dataIndex: "companyName", width: 120 },
        { title: "Poste", dataIndex: "jobTitle", width: 120 },
        {
          title: "Contrats", width: 130,
          render: (_, r) => (
            <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              {r.hasContratSante && <Tag color="blue" style={{ fontSize: 10 }}>Santé</Tag>}
              {r.hasContratPrevoyance && <Tag color="green" style={{ fontSize: 10 }}>Prév.</Tag>}
              {r.hasContratDeces && <Tag color="purple" style={{ fontSize: 10 }}>Décès</Tag>}
              {!r.hasContratSante && !r.hasContratPrevoyance && !r.hasContratDeces && <span style={{ color: "#9ca3af" }}>—</span>}
            </div>
          ),
        },
        { title: "Cotisation", dataIndex: "cotisationMensuelle", width: 80, render: (v) => v ? `${v} €` : "—" },
        { title: "Régime", dataIndex: "regime", width: 100, render: (v) => v || "—" },
        { title: "Dép.", dataIndex: "departement", width: 50 },
        {
          title: "", width: 70,
          render: (_, r) => (
            <Button type="text" danger size="small" onClick={() => handleDelete(r.id)}>
              Supprimer
            </Button>
          ),
        },
      ]} />

      <Modal title="Nouveau lead" open={createOpen} onCancel={() => setCreateOpen(false)} onOk={handleCreate} okText="Créer" cancelText="Annuler" width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="email" label="Email" rules={[{ required: true, message: "L'email est requis" }]}>
            <Input placeholder="lead@example.com" />
          </Form.Item>
          <div className="d-flex" style={{ gap: "12px" }}>
            <Form.Item name="firstName" label="Prénom" style={{ flex: 1 }}>
              <Input placeholder="Jean" />
            </Form.Item>
            <Form.Item name="lastName" label="Nom" style={{ flex: 1 }}>
              <Input placeholder="Dupont" />
            </Form.Item>
          </div>
          <Form.Item name="companyName" label="Entreprise">
            <Input placeholder="Acme Corp" />
          </Form.Item>
          <div className="d-flex" style={{ gap: "12px" }}>
            <Form.Item name="jobTitle" label="Poste" style={{ flex: 1 }}>
              <Input placeholder="CTO" />
            </Form.Item>
            <Form.Item name="phone" label="Téléphone" style={{ flex: 1 }}>
              <Input placeholder="+33 6 12 34 56 78" />
            </Form.Item>
          </div>
          <Card size="small" title="Segmentation" style={{ marginBottom: 16 }}>
            <div className="d-flex" style={{ gap: "12px", marginBottom: 12 }}>
              <Form.Item name="hasContratSante" label="Contrat santé" valuePropName="checked" style={{ flex: 1 }}>
                <Switch size="small" />
              </Form.Item>
              <Form.Item name="hasContratPrevoyance" label="Contrat prévoyance" valuePropName="checked" style={{ flex: 1 }}>
                <Switch size="small" />
              </Form.Item>
              <Form.Item name="hasContratDeces" label="Contrat décès" valuePropName="checked" style={{ flex: 1 }}>
                <Switch size="small" />
              </Form.Item>
            </div>
            <div className="d-flex" style={{ gap: "12px" }}>
              <Form.Item name="cotisationMensuelle" label="Cotisation (€/mois)" style={{ flex: 1 }}>
                <InputNumber min={0} step={1} style={{ width: "100%" }} placeholder="90" />
              </Form.Item>
              <Form.Item name="regime" label="Régime" style={{ flex: 1 }}>
                <Select allowClear placeholder="Sélectionner">
                  <Select.Option value="SECURITE_SOCIALE">Sécu. Sociale</Select.Option>
                  <Select.Option value="ALSACE_MOSELLE">Alsace-Moselle</Select.Option>
                  <Select.Option value="REGIME_SPECIAL">Régime Spécial</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="departement" label="Département" style={{ flex: 1 }}>
                <Input placeholder="75" />
              </Form.Item>
            </div>
          </Card>
        </Form>
      </Modal>
    </>
  );
};

export default List;
